import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import Carousel from 'LuxKit/Carousel/Carousel'
import FilterChip, { FILTER_CHIP_HEIGHTS } from 'LuxKit/Chips/FilterChip'
import InputChip from 'LuxKit/Chips/InputChip'
import CheckboxInputListItem from 'LuxKit/List/CheckboxInputListItem'
import LineFilterAltIcon from 'LuxKit/Icons/line/LineFilterAltIcon'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineAngleUpIcon from 'LuxKit/Icons/line/LineAngleUpIcon'
import LineAwardIcon from 'LuxKit/Icons/line/LineAwardIcon'
import LineStarIcon from 'LuxKit/Icons/line/LineStarIcon'
import LineGiftIcon from 'LuxKit/Icons/line/LineGiftIcon'
import RadioInput from 'LuxKit/Radio/RadioInput'
import Group from 'components/utils/Group'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import BodyText from 'LuxKit/Typography/BodyText'
import useToggle from 'hooks/useToggle'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import useExperiencesSearchFilters from 'components/Experiences/hooks/useExperiencesSearchFilters'
import useCustomerRatingOptions from 'hooks/Search/useCustomerRatingOptions'
import useMostPopulatedSearchExperienceFacets, {
  type CategoryData,
} from '../hooks/useMostPopulatedSearchExperienceFacets'
import { arrayToObject } from 'lib/array/arrayUtils'
import {
  EXP_CATEGORY_LUX_PLUS_ID,
  EXP_CATEGORY_LUX_PLUS_MEMBER_PRICES_ID,
  EXP_CATEGORY_LUX_EXCLUSIVE_ID,
  EXP_CATEGORY_GIFT_INSPIRATION,
} from 'constants/experience'
import ExperienceSearchPriceFilter from './ExperienceSearchPriceFilter'
import useActiveExperienceFilterChips from '../hooks/useActiveExperienceFilterChips'

const CHIP_HEIGHT = FILTER_CHIP_HEIGHTS.medium

const StyledInputChip = styled(InputChip)`
  height: ${rem(CHIP_HEIGHT)};
`

const DropdownPadding = styled.div`
  padding-inline: ${rem(16)};
`

const PriceDropdownContent = styled.div`
  padding: ${rem(16)};
  min-width: ${rem(280)};
`

const EXCLUDED_QUICK_FILTER_CATEGORY_IDS = [
  EXP_CATEGORY_LUX_PLUS_ID,
  EXP_CATEGORY_LUX_PLUS_MEMBER_PRICES_ID,
  EXP_CATEGORY_LUX_EXCLUSIVE_ID,
  EXP_CATEGORY_GIFT_INSPIRATION,
]

interface CategoryFilterChipProps {
  categoryData: CategoryData;
  categoryFacetsById: Record<number, App.ExperienceCategoryFacet>;
  selectedCategoryCodes: Array<number>;
  onApply: (
    parentId: number,
    childIds: Array<number>,
    selectedIds: Set<number>,
  ) => void;
  onReset: (parentId: number, childIds: Array<number>) => void;
  isMobile: boolean;
}

function CategoryFilterChip({
  categoryData,
  categoryFacetsById,
  selectedCategoryCodes,
  onApply,
  onReset,
  isMobile,
}: CategoryFilterChipProps) {
  const intl = useIntl()
  const [isOpen, toggleOpen, , closeDropdown] = useToggle()
  const chipRef = useRef<HTMLButtonElement>(null)

  const childCategories = categoryData.children
  const parentId = categoryData.parent.id
  const childIds = useMemo(
    () => childCategories.map((c) => c.id),
    [childCategories],
  )

  const allRelatedIds = useMemo(
    () => [parentId, ...childIds],
    [parentId, childIds],
  )
  const hasSelection = allRelatedIds.some((id) =>
    selectedCategoryCodes.includes(id),
  )

  const [pendingChecked, setPendingChecked] = useState<Set<number>>(() => {
    const isParentSelected = selectedCategoryCodes.includes(parentId)
    if (isParentSelected) {
      return new Set(childIds)
    }
    return new Set(childIds.filter((id) => selectedCategoryCodes.includes(id)))
  })

  useEffect(() => {
    if (isOpen) {
      const isParentSelected = selectedCategoryCodes.includes(parentId)
      if (isParentSelected) {
        setPendingChecked(new Set(childIds))
      } else {
        setPendingChecked(
          new Set(childIds.filter((id) => selectedCategoryCodes.includes(id))),
        )
      }
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const handleToggle = useCallback((value: number) => {
    setPendingChecked((prev) => {
      const next = new Set(prev)
      if (next.has(value)) {
        next.delete(value)
      } else {
        next.add(value)
      }
      return next
    })
  }, [])

  const handleApply = useCallback(() => {
    onApply(parentId, childIds, pendingChecked)
    closeDropdown()
  }, [parentId, childIds, pendingChecked, onApply, closeDropdown])

  const handleReset = useCallback(() => {
    onReset(parentId, childIds)
    closeDropdown()
  }, [parentId, childIds, onReset, closeDropdown])

  const applyText = intl.formatMessage({
    id: 'XGAxBfjicX0x40wpm7Xt',
    defaultMessage: 'Apply',
    description:
      'Button text to apply sort and filter selections in experience search',
  })

  const resetText = intl.formatMessage({
    id: '1BCuuKA_YbmI7wo_U3rn',
    defaultMessage: 'Reset',
    description:
      'Link to reset filter selections in the search or listing filters panel',
  })

  const visibleChildren = useMemo(
    () =>
      childCategories.filter((c) => (categoryFacetsById[c.id]?.count ?? 0) > 0),
    [childCategories, categoryFacetsById],
  )

  const allSelected = useMemo(
    () =>
      visibleChildren.length > 0 &&
      visibleChildren.every((c) => pendingChecked.has(c.id)),
    [visibleChildren, pendingChecked],
  )

  const handleSelectAll = useCallback(() => {
    setPendingChecked((prev) => {
      const next = new Set(prev)
      if (visibleChildren.every((c) => next.has(c.id))) {
        visibleChildren.forEach((c) => next.delete(c.id))
      } else {
        visibleChildren.forEach((c) => next.add(c.id))
      }
      return next
    })
  }, [visibleChildren])

  const checkboxList = (
    <>
      {visibleChildren.length > 0 && (
        <CheckboxInputListItem
          inputPlacement="start"
          checked={allSelected}
          onChange={handleSelectAll}
          title={intl.formatMessage({
            id: 'VTp-nGNzNfgb6HumlrZx',
            defaultMessage: 'Select all',
            description:
              'Checkbox to select all subcategory options in a category filter dropdown',
          })}
          size="medium"
        />
      )}
      {visibleChildren.map((child) => {
        const count = categoryFacetsById[child.id]?.count ?? 0
        return (
          <CheckboxInputListItem
            key={child.id}
            inputPlacement="start"
            checked={pendingChecked.has(child.id)}
            onChange={() => handleToggle(child.id)}
            title={child.name}
            size="medium"
          >
            <BodyText variant="medium" colour="neutral-two">
              {intl.formatMessage(
                {
                  id: 'CV8utsl6cVxsfdk50i1a',
                  defaultMessage: '({count})',
                  description:
                    'Count of available items shown next to a category filter option',
                },
                { count },
              )}
            </BodyText>
          </CheckboxInputListItem>
        )
      })}
    </>
  )

  return (
    <div>
      <FilterChip
        ref={chipRef}
        size="medium"
        selected={hasSelection}
        onClick={toggleOpen}
        endIcon={isOpen ? <LineAngleUpIcon /> : <LineAngleDownIcon />}
      >
        {categoryData.parent.name}
      </FilterChip>
      <CSSBreakpoint min="tablet">
        <DropdownList
          size="S"
          width="max"
          triggerRef={chipRef}
          anchorRef={chipRef}
          open={isOpen}
          placement="bottom-start"
          onClose={closeDropdown}
          title={categoryData.parent.name}
          hasDismissButton
          primaryActionProps={{
            children: applyText,
            onClick: handleApply,
            style: { flex: 1 },
          }}
          secondaryActionProps={{
            kind: 'tertiary',
            children: resetText,
            onClick: handleReset,
          }}
        >
          {checkboxList}
        </DropdownList>
      </CSSBreakpoint>
      <CSSBreakpoint max="mobile">
        <ModalBase isOpen={isOpen && isMobile} onClose={closeDropdown}>
          <ModalHeader
            title={categoryData.parent.name}
            onCloseButtonClick={closeDropdown}
          />
          <ModalBody>
            <ModalContent flushListItems>{checkboxList}</ModalContent>
          </ModalBody>
          <ModalFooter
            primaryActionProps={{
              children: applyText,
              onClick: handleApply,
              style: { flexGrow: 2 },
            }}
            secondaryActionProps={{
              children: resetText,
              onClick: handleReset,
              style: { flexGrow: 0 },
            }}
          />
        </ModalBase>
      </CSSBreakpoint>
    </div>
  )
}

interface Props {
  toggleFilterPanel: () => void;
  place?: App.Place;
}

function ExperienceExposedFiltersHeader({ toggleFilterPanel, place }: Props) {
  const intl = useIntl()
  const isMobile = useScreenSizeOnly('mobile')
  const { searchFilters, setSearchFilters, setCategoryCodesSearchFilter } =
    useExperiencesSearchFilters()
  const { CUSTOMER_RATING_GTE } = useCustomerRatingOptions()
  const { primaryCategories, categories: categoryFacets, luxPlusMemberPriceCount } =
    useMostPopulatedSearchExperienceFacets(place)

  const [isPriceOpen, togglePrice, , closePrice] = useToggle()
  const [isRatingOpen, toggleRating, , closeRating] = useToggle()

  const priceChipRef = useRef<HTMLButtonElement>(null)
  const ratingChipRef = useRef<HTMLButtonElement>(null)

  const customerRatingOptions = useMemo(
    () => [
      CUSTOMER_RATING_GTE.EXCEPTIONAL,
      CUSTOMER_RATING_GTE.VERY_GOOD,
      CUSTOMER_RATING_GTE.GOOD,
      CUSTOMER_RATING_GTE.ANY_RATING,
    ],
    [CUSTOMER_RATING_GTE],
  )

  const selectedRating = searchFilters.minRating ?? 0
  const [pendingRating, setPendingRating] = useState(selectedRating)

  useEffect(() => {
    if (isRatingOpen) {
      setPendingRating(selectedRating)
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [isRatingOpen])

  const onRatingChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setPendingRating(Number(e.target.value))
    },
    [],
  )

  const handleRatingApply = useCallback(() => {
    setSearchFilters({
      ...searchFilters,
      minRating: pendingRating === 0 ? undefined : pendingRating,
    })
    closeRating()
  }, [pendingRating, searchFilters, setSearchFilters, closeRating])

  const handleRatingReset = useCallback(() => {
    setSearchFilters({ ...searchFilters, minRating: undefined })
    setPendingRating(0)
    closeRating()
  }, [searchFilters, setSearchFilters, closeRating])

  const isLuxPlusSelected = useMemo(
    () => !!searchFilters.luxPlusFeatures?.length,
    [searchFilters.luxPlusFeatures],
  )
  const isLuxExclusiveSelected = useMemo(
    () => searchFilters.leExclusive ?? false,
    [searchFilters.leExclusive],
  )
  const isGiftSelected = useMemo(
    () => searchFilters.featured?.includes('is-gift') ?? false,
    [searchFilters.featured],
  )

  const onToggleLuxPlus = useCallback(() => {
    setSearchFilters({
      ...searchFilters,
      luxPlusFeatures: isLuxPlusSelected ? undefined : ['Member prices'],
    })
  }, [isLuxPlusSelected, searchFilters, setSearchFilters])

  const onToggleLuxExclusive = useCallback(() => {
    setSearchFilters({
      ...searchFilters,
      leExclusive: isLuxExclusiveSelected ? undefined : true,
    })
  }, [isLuxExclusiveSelected, searchFilters, setSearchFilters])

  const onToggleGiftInspiration = useCallback(() => {
    const next = isGiftSelected
      ? (searchFilters.featured ?? []).filter(
          (featured) => featured !== 'is-gift',
        )
      : [
          ...(searchFilters.featured ?? []),
          'is-gift' as Experiences.FeaturedFilter,
        ]
    setSearchFilters({
      ...searchFilters,
      featured: next.length > 0 ? next : undefined,
    })
  }, [isGiftSelected, searchFilters, setSearchFilters])

  const topLevelCategories = useMemo(() => {
    return primaryCategories.filter(
      (c) => !EXCLUDED_QUICK_FILTER_CATEGORY_IDS.includes(c.parent.id),
    )
  }, [primaryCategories])

  const categoryFacetsById = useMemo(
    () => arrayToObject(categoryFacets, (f) => f.categoryId),
    [categoryFacets],
  )

  const onCategoryApply = useCallback(
    (parentId: number, childIds: Array<number>, selectedIds: Set<number>) => {
      const current = searchFilters.categoryCodes ?? []
      const allRelated = new Set([parentId, ...childIds])
      const otherCategories = current.filter((id) => !allRelated.has(id))
      setCategoryCodesSearchFilter([...otherCategories, ...selectedIds])
    },
    [searchFilters.categoryCodes, setCategoryCodesSearchFilter],
  )

  const onCategoryReset = useCallback(
    (parentId: number, childIds: Array<number>) => {
      const current = searchFilters.categoryCodes ?? []
      const allRelated = new Set([parentId, ...childIds])
      setCategoryCodesSearchFilter(current.filter((id) => !allRelated.has(id)))
    },
    [searchFilters.categoryCodes, setCategoryCodesSearchFilter],
  )

  const selectedCategoryCodes = searchFilters.categoryCodes ?? []

  const hasPriceSelected = !!(searchFilters.priceGte || searchFilters.priceLte)
  const hasRatingSelected = selectedRating > 0

  const priceLabel = intl.formatMessage({
    id: 'uhzheYPeu42wS_U04j7E',
    defaultMessage: 'Price',
    description:
      'Title for the price filter group in experience search filters',
  })

  const ratingLabel = intl.formatMessage({
    id: 'XGkSHbOIT8muMeKg7j0c',
    defaultMessage: 'Customer rating',
    description:
      'Title for the rating filter group in experience search filters',
  })

  const allFiltersLabel = intl.formatMessage({
    id: '-lpIiE7WOtNg4S1Miji_',
    defaultMessage: 'Sort & Filters',
    description:
      'Label for the sort and filters chip in experience search exposed filters',
  })

  const applyText = intl.formatMessage({
    id: 'XGAxBfjicX0x40wpm7Xt',
    defaultMessage: 'Apply',
    description:
      'Button text to apply sort and filter selections in experience search',
  })

  const resetText = intl.formatMessage({
    id: '1BCuuKA_YbmI7wo_U3rn',
    defaultMessage: 'Reset',
    description:
      'Link to reset filter selections in the search or listing filters panel',
  })

  const ratingChipLabel =
    ratingLabel + (hasRatingSelected ? ` (${selectedRating}.0+)` : '')

  const recentFilterChips = useActiveExperienceFilterChips()

  const makeRatingContent = (groupName: string) => (
    <Group direction="vertical" gap={16}>
      {customerRatingOptions.map((option) => (
        <RadioInput
          key={option.label}
          checked={option.value === pendingRating}
          onChange={onRatingChange}
          name={groupName}
          value={option.value}
        >
          {option.label}
        </RadioInput>
      ))}
    </Group>
  )

  return (
    <Carousel gap={8} gutterStyle="overflow" gutterSize={16}>
      <FilterChip
        size="medium"
        startIcon={<LineFilterAltIcon />}
        onClick={toggleFilterPanel}
      >
        {allFiltersLabel}
      </FilterChip>

      {recentFilterChips.map((chip) => (
        <StyledInputChip
          key={chip.key}
          variant="filled"
          onClick={chip.onRemove}
        >
          {chip.label}
        </StyledInputChip>
      ))}

      <FilterChip
        size="medium"
        selected={isLuxPlusSelected}
        startIcon={<LineAwardIcon />}
        onClick={onToggleLuxPlus}
        disabled={luxPlusMemberPriceCount === 0}
      >
        {intl.formatMessage({
          id: '0ueZLLyLio0zL-YrpCK5',
          defaultMessage: 'Lux Plus',
          description:
            'Toggle chip to filter experiences by Lux Plus on mobile and tablet search',
        })}
      </FilterChip>
      <FilterChip
        size="medium"
        selected={isLuxExclusiveSelected}
        startIcon={<LineStarIcon />}
        onClick={onToggleLuxExclusive}
      >
        {intl.formatMessage({
          id: 'Cjb54kgiXFwLdx68lII6',
          defaultMessage: 'Lux exclusive',
          description:
            'Toggle chip to filter experiences by Lux Exclusive on mobile and tablet search',
        })}
      </FilterChip>
      <FilterChip
        size="medium"
        selected={isGiftSelected}
        startIcon={<LineGiftIcon />}
        onClick={onToggleGiftInspiration}
      >
        {intl.formatMessage({
          id: '4DyBR7kFhZSpjBGSWYnZ',
          defaultMessage: 'Gift inspiration',
          description:
            'Toggle chip to filter experiences by gift inspiration on mobile and tablet search',
        })}
      </FilterChip>

      <div>
        <FilterChip
          ref={priceChipRef}
          size="medium"
          selected={hasPriceSelected}
          onClick={togglePrice}
          endIcon={isPriceOpen ? <LineAngleUpIcon /> : <LineAngleDownIcon />}
        >
          {priceLabel}
        </FilterChip>
        <CSSBreakpoint min="tablet">
          <DropdownList
            size="S"
            triggerRef={priceChipRef}
            anchorRef={priceChipRef}
            open={isPriceOpen}
            placement="bottom-start"
            onClose={closePrice}
          >
            <PriceDropdownContent>
              <ExperienceSearchPriceFilter place={place} />
            </PriceDropdownContent>
          </DropdownList>
        </CSSBreakpoint>
        <CSSBreakpoint max="mobile">
          <ModalBase isOpen={isPriceOpen && isMobile} onClose={closePrice}>
            <ModalHeader title={priceLabel} onCloseButtonClick={closePrice} />
            <ModalBody>
              <ModalContent>
                <ExperienceSearchPriceFilter place={place} />
              </ModalContent>
            </ModalBody>
            <ModalFooter
              primaryActionProps={{
                children: applyText,
                onClick: closePrice,
                style: { flexGrow: 2 },
              }}
              secondaryActionProps={{
                children: resetText,
                onClick: () => {
                  setSearchFilters({
                    ...searchFilters,
                    priceGte: undefined,
                    priceLte: undefined,
                  })
                  closePrice()
                },
                style: { flexGrow: 0 },
              }}
            />
          </ModalBase>
        </CSSBreakpoint>
      </div>

      <div>
        <FilterChip
          ref={ratingChipRef}
          size="medium"
          selected={hasRatingSelected}
          onClick={toggleRating}
          endIcon={isRatingOpen ? <LineAngleUpIcon /> : <LineAngleDownIcon />}
        >
          {ratingChipLabel}
        </FilterChip>
        <CSSBreakpoint min="tablet">
          <DropdownList
            size="S"
            triggerRef={ratingChipRef}
            anchorRef={ratingChipRef}
            open={isRatingOpen}
            placement="bottom-start"
            onClose={closeRating}
            title={ratingLabel}
            hasDismissButton
            primaryActionProps={{
              children: applyText,
              onClick: handleRatingApply,
              style: { flex: 1 },
            }}
            secondaryActionProps={{
              kind: 'tertiary',
              children: resetText,
              onClick: handleRatingReset,
            }}
          >
            <DropdownPadding>{makeRatingContent('customer-rating-tablet')}</DropdownPadding>
          </DropdownList>
        </CSSBreakpoint>
        <CSSBreakpoint max="mobile">
          <ModalBase isOpen={isRatingOpen && isMobile} onClose={closeRating}>
            <ModalHeader title={ratingLabel} onCloseButtonClick={closeRating} />
            <ModalBody>
              <ModalContent>{makeRatingContent('customer-rating-mobile')}</ModalContent>
            </ModalBody>
            <ModalFooter
              primaryActionProps={{
                children: applyText,
                onClick: handleRatingApply,
                style: { flexGrow: 2 },
              }}
              secondaryActionProps={{
                children: resetText,
                onClick: handleRatingReset,
                style: { flexGrow: 0 },
              }}
            />
          </ModalBase>
        </CSSBreakpoint>
      </div>

      {topLevelCategories.map((cat) => (
        <CategoryFilterChip
          key={cat.parent.id}
          categoryData={cat}
          categoryFacetsById={categoryFacetsById}
          selectedCategoryCodes={selectedCategoryCodes}
          onApply={onCategoryApply}
          onReset={onCategoryReset}
          isMobile={isMobile}
        />
      ))}
    </Carousel>
  )
}

export default ExperienceExposedFiltersHeader
